import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import CommonFilterModal from "../../../components/Common/CommonFilterModal";
import DoctorsFilterOptions from "../../../components/Common/CommonFilterOptions";
import Deletepopup from "../../../components/Modal/Deletepopup";
import { DocterBreadcrumbs, statuses } from "../Utils/MasterConst";

// Updated custom hook paths
import Actions from "../../../components/Common/Actions";
import FilledButton from "../../../components/Common/Buttons/FilledButton";
import ListPageLayout from "../../../components/Layout";
import useDebouncedSearch from "../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../components/Utils/hooks/useSortHandler";

const Others = () => {
  const navigate = useNavigate();
  const addOthers = {
    id: "addnewpost",
    onClick: () => navigate("/master-others-form"),
    style: { width: "240px" },
    label: "Add Others",
  };
  // Sorting
  const [sort, setSort] = useState("");
  const [sortName, setSortName] = useState("Newest First");
  const handleSortClick = useSortHandler(setSort, setSortName);
  const [loader, setLoader] = useState(true);

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 })),
  );

  // Filter states
  const [selectedValues, setSelectedValues] = useState({
    Others: [],
    Status: [],
  });
  const [filteringKey, setFilteringKey] = useState([]);
  const [tempSelectedValues, setTempSelectedValues] = useState({
    Others: [],
    Status: [],
  });

  const [selectedCategory, setSelectedCategory] = useState("Others");
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);

  // Delete state
  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  // Pagination
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}types/list`,
      initialParams: {
        sort,
        searchText: search,
        selectedValues,
      },
    });

  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler("types", fetchData, setShowDelete);

  const fetchFilteringArray = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}types/getarray`,
      );
      setFilteringKey(res.data);
    } catch (err) {
      console.error(err);
      alert("Error loading filter options");
    }
  }, []);

  useEffect(() => {
    fetchFilteringArray();
  }, [fetchFilteringArray]);

  useEffect(() => {
    setLoader(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, pagination.limit, sort, search, selectedValues]);

  const columns = [
    {
      title: "S.No",
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
      key: "serial",
      width: 80,
      fixed: "left",
    },
    {
      title: "Types",
      width: 250,
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      width: 150,
      render: (active) => (active ? "Verified" : "Not Verified"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="d-flex align-items-center">
          <Actions
            isEdit={true}
            isDelete={true}
            isView={true}
            onClickView={() => navigate(`/master-others-view/${record._id}`)}
            onClickDelete={() => {
              setDeleteId(record._id);
              setShowDelete(true);
            }}
            onClickEdit={() => navigate(`/master-others-edit/${record._id}`)}
          />
        </div>
      ),
    },
  ];

  const dataSource = records.map((el) => ({ ...el, key: el._id }));

  const filters = {
    Others: filteringKey,
    Status: statuses,
  };

  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);

  return (
    <ListPageLayout
      breadcrumbs={<DocterBreadcrumbs last="Others" />}
      addButton={<FilledButton props={addOthers} />}
      titles={null}
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          setLimit={(val) =>
            setPagination((prev) => ({
              ...prev,
              limit: val,
              skip: 0,
              currentPage: 1,
            }))
          }
          search={search}
          onChange={debouncedSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          filter_records={() => {}}
          setFilterShow={() => setIsFilterModalVisible(true)}
        />
      }
      tableProps={{
        columns,
        dataSource,
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={(selected) => handlePageChange({ selected })}
      loader={loader}
    >
      {/* Optional children like modals */}
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}

      <CommonFilterModal
        visible={isFilterModalVisible}
        onClose={() => setIsFilterModalVisible(false)}
        onApply={() => {
          setSelectedValues(tempSelectedValues);
          setIsFilterModalVisible(false);
          setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
          fetchData();
        }}
        onReset={() => {
          setTempSelectedValues({ Others: [], Status: [] });
          setSelectedValues({ Others: [], Status: [] });
          setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
          fetchData();
        }}
        filters={filters}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedValues={tempSelectedValues}
        setSelectedValues={setTempSelectedValues}
      />
    </ListPageLayout>
  );
};

export default Others;
